import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { usePermissions } from '@/hooks/usePermissions';
import { Permission } from '@/constants/permissions';
import { useNotificationStore } from '@/stores/notificationStore';
import { useAuthStore } from '@/stores/authStore';

interface PermissionProtectedRouteProps {
  children: React.ReactNode;
  /**
   * Single permission required to access this route
   */
  permission?: Permission;
  /**
   * User needs ANY of these permissions to access (OR logic)
   */
  anyPermissions?: Permission[];
  /**
   * User needs ALL of these permissions to access (AND logic)
   */
  allPermissions?: Permission[];
  /**
   * Custom message to show when access is denied
   */
  deniedMessage?: string;
}

/**
 * PermissionProtectedRoute
 * 
 * Wraps routes that require specific permissions. Redirects to /forbidden
 * if the user doesn't have the required permissions and shows a notification.
 * 
 * Usage:
 * ```tsx
 * <Route path="/members" element={
 *   <PermissionProtectedRoute anyPermissions={[PERMISSIONS.VIEW_USERS, PERMISSIONS.MANAGE_USERS]}>
 *     <MembersPage />
 *   </PermissionProtectedRoute>
 * } />
 * ```
 */
export function PermissionProtectedRoute({
  children,
  permission,
  anyPermissions,
  allPermissions,
  deniedMessage,
}: PermissionProtectedRouteProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [hasChecked, setHasChecked] = React.useState(false);
  const [hasNavigated, setHasNavigated] = React.useState(false);

  // CRITICAL: All hooks must be called unconditionally (Rules of Hooks)
  // Call usePermissions even if user is not authenticated
  const { hasPermission, hasAnyPermission, hasAllPermissions, isLoading } = usePermissions();

  // Check permissions (must be before any conditional returns)
  let hasAccess = false;
  
  // Only check permissions if user is authenticated
  if (user) {
    if (permission) {
      hasAccess = hasPermission(permission);
    } else if (anyPermissions) {
      hasAccess = hasAnyPermission(anyPermissions);
    } else if (allPermissions) {
      hasAccess = hasAllPermissions(allPermissions);
    } else {
      // No permission specified - deny by default for safety
      hasAccess = false;
    }
  }

  // Handle permission denial with navigation in useEffect to prevent infinite loops
  // IMPORTANT: All hooks must be called before any conditional returns
  React.useEffect(() => {
    // Only proceed if user is authenticated, not loading, and hasn't navigated yet
    if (!user || isLoading || hasNavigated) {
      return;
    }

    // If user doesn't have access and we haven't already shown notification
    if (!hasAccess && !hasChecked) {
      setHasChecked(true);
      const message = deniedMessage || 'You don\'t have permission to access this page';
      useNotificationStore.getState().warning(message, {
        duration: 5000,
      });

      // Navigate to forbidden page ONCE using useEffect
      // This prevents the infinite loop caused by rendering <Navigate> multiple times
      setHasNavigated(true);
      navigate('/forbidden', {
        replace: true,
        state: {
          from: location.pathname,
          message,
        },
      });
    }
  }, [isLoading, hasAccess, hasChecked, deniedMessage, user, hasNavigated, navigate, location.pathname]);

  // NOW we can do conditional returns - all hooks have been called

  // If user is not authenticated, render children and let the route loader
  // handle the redirect to /auth. This prevents infinite loops.
  if (!user) {
    return <>{children}</>;
  }

  // Show loading state while permissions are being fetched
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  // Show loading while navigation is happening to prevent flicker
  // The actual navigation happens in useEffect above
  if (!hasAccess && hasNavigated) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  // User has permission - render children
  return <>{children}</>;
}

